import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { isPropOrGuardianUser } from "../../../utils/userRoles";
import VegetationSuppressionUnderConstruction from "./UnderConstruction";
import Sidebar from "../../sidebar";
import { useModalContext } from "../../CustomModal/ModalProvider.jsx";
import {
  MainContainer,
  SideContainer,
  ContentWrapper,
  Title,
  HeaderSection,
  StyledButtonForm,
  TeamList,
  TeamChip,
} from "../DetailsArea/styles";
import {
  EditorLayout,
  FormGrid,
  Field,
  Hint,
  AreaInfoCard,
  AreaInfoTitle,
  AreaInfoGrid,
  AreaInfoItem,
  AreaInfoLabel,
  AreaInfoValue,
  FormActions,
} from "./styles";
import TiffSuppressionEditor from "./Editor/TiffSuppressionEditor";
import { vegetationSuppressionService } from "../../../services/vegetationSuppressionService";
import { guardianAreaService } from "../../../services/guardianAreaService";

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const resolvePersonName = (value, nameFallback) => {
  if (nameFallback) return String(nameFallback);
  if (value && typeof value === "object") {
    return value.full_name || value.name || "";
  }
  if (typeof value === "string" && value.trim() && !/^\d+$/.test(value.trim())) {
    return value.trim();
  }
  return "";
};

const buildTeam = (area) => {
  const team = [];
  const seen = new Set();

  const pushMember = (name, role) => {
    const label = (name || "").trim();
    if (!label) return;
    const key = `${label.toLowerCase()}|${role}`;
    if (seen.has(key)) return;
    seen.add(key);
    team.push({ name: label, role });
  };

  pushMember(
    resolvePersonName(area?.vert_responsible, area?.vert_responsible_name),
    "VERT"
  );
  pushMember(
    resolvePersonName(area?.adi_responsible, area?.adi_responsible_name),
    "Gestor"
  );

  const others = Array.isArray(area?.other_responsibles)
    ? area.other_responsibles
    : [];
  others.forEach((person) => {
    pushMember(resolvePersonName(person, person?.full_name || person?.name), "Equipe");
  });

  return team;
};

const VegetationSuppressionForm = () => {
  const { id_code, recordId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useModalContext();
  const collapsed = useSelector((state) => state.sidebar);
  const currentUser = useSelector((state) => state.user.currentUser);
  const isEdit = Boolean(recordId);

  const [areaInfo, setAreaInfo] = useState(null);
  const [tiffFile, setTiffFile] = useState(null);
  const [record, setRecord] = useState(null);
  const [geometry, setGeometry] = useState({ type: "FeatureCollection", features: [] });
  const [imageMeta, setImageMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    notes: "",
    observation_date: "",
    area_ha: "",
    status: "draft",
  });

  const goBack = () =>
    navigate(`/area_details/${id_code}`, { state: { activeTab: "asv" } });

  useEffect(() => {
    if (isPropOrGuardianUser(currentUser)) return;
    const load = async () => {
      try {
        const areaRes = await guardianAreaService.getGuardianAreaById(id_code);
        const a = areaRes?.data || areaRes;
        setAreaInfo({
          name: a?.name || "",
          project:
            a?.project_detail?.title ||
            a?.project_name ||
            (typeof a?.project === "object" ? a?.project?.title || a?.project?.name : a?.project) ||
            "",
          total_area: a?.total_area,
          sicar_code: a?.sicar_code || "",
          team: buildTeam(a),
        });
      } catch {
        setAreaInfo({ name: "", team: [] });
      }
      if (!recordId) return;
      try {
        const data = await vegetationSuppressionService.get(id_code, recordId);
        setRecord(data);
        setForm({
          title: data.title || "",
          notes: data.notes || "",
          observation_date: data.observation_date || "",
          area_ha: data.area_ha != null ? String(data.area_ha) : "",
          status: data.status || "draft",
        });
        setGeometry(
          data.suppression_geometry || { type: "FeatureCollection", features: [] }
        );
      } catch (err) {
        showError("Erro", "Não foi possível carregar o registro.");
      }
    };
    load();
  }, [id_code, recordId, showError, currentUser]);

  const tiffUrl = useMemo(() => {
    if (tiffFile) return null;
    return record?.original_tiff_url || null;
  }, [record, tiffFile]);

  const onField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleGeometry = (fc, meta) => {
    setGeometry(fc);
    if (meta) setImageMeta(meta);
  };

  const handleSave = async () => {
    if (!form.observation_date) {
      showError("Atenção", "Informe a data da observação (cena).");
      return;
    }
    if (!isEdit && !tiffFile) {
      showError("Atenção", "Selecione o arquivo TIFF.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        notes: form.notes,
        observation_date: form.observation_date,
        status: form.status,
        suppression_geometry: geometry,
      };
      if (form.area_ha !== "") payload.area_ha = Number(form.area_ha);
      if (imageMeta?.imageWidth) payload.image_width = imageMeta.imageWidth;
      if (imageMeta?.imageHeight) payload.image_height = imageMeta.imageHeight;

      if (isEdit) {
        await vegetationSuppressionService.update(id_code, recordId, payload);
        showSuccess("Salvo", "Indicação de supressão atualizada.");
      } else {
        await vegetationSuppressionService.create(id_code, payload, tiffFile);
        showSuccess("Cadastrado", "Supressão registrada com o TIFF original.");
      }
      goBack();
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.original_tiff?.[0] ||
        err?.response?.data?.detail ||
        err?.response?.data?.observation_date?.[0] ||
        "Falha ao salvar. Verifique o arquivo e os campos.";
      showError("Erro", typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  if (isPropOrGuardianUser(currentUser)) {
    return (
      <MainContainer>
        <Sidebar />
        <SideContainer collapsed={collapsed}>
          <ContentWrapper>
            <VegetationSuppressionUnderConstruction />
          </ContentWrapper>
        </SideContainer>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <ContentWrapper style={{ paddingTop: "2rem" }}>
          <HeaderSection>
            <Title>
              {isEdit ? "Editar supressão de vegetação" : "Nova supressão de vegetação"}
            </Title>
            <Hint>
              Envie o TIFF da cena, marque o polígono da supressão sobre a imagem
              e salve. O arquivo original fica armazenado e a indicação fica
              vinculada à data da observação.
            </Hint>
          </HeaderSection>

          <AreaInfoCard>
            <AreaInfoTitle>Área vinculada</AreaInfoTitle>
            <AreaInfoGrid>
              <AreaInfoItem>
                <AreaInfoLabel>Nome</AreaInfoLabel>
                <AreaInfoValue>
                  {displayValue(areaInfo?.name || "Carregando…")}
                </AreaInfoValue>
              </AreaInfoItem>
              <AreaInfoItem>
                <AreaInfoLabel>Projeto</AreaInfoLabel>
                <AreaInfoValue>{displayValue(areaInfo?.project)}</AreaInfoValue>
              </AreaInfoItem>
              <AreaInfoItem>
                <AreaInfoLabel>Área total (ha)</AreaInfoLabel>
                <AreaInfoValue>
                  {areaInfo?.total_area != null && areaInfo.total_area !== ""
                    ? String(areaInfo.total_area)
                    : "—"}
                </AreaInfoValue>
              </AreaInfoItem>
              <AreaInfoItem>
                <AreaInfoLabel>Código SICAR</AreaInfoLabel>
                <AreaInfoValue>{displayValue(areaInfo?.sicar_code)}</AreaInfoValue>
              </AreaInfoItem>
              <AreaInfoItem data-full="true">
                <AreaInfoLabel>Equipe participante</AreaInfoLabel>
                {areaInfo?.team?.length > 0 ? (
                  <TeamList>
                    {areaInfo.team.map((member) => (
                      <TeamChip key={`${member.role}-${member.name}`}>
                        {member.name} · {member.role}
                      </TeamChip>
                    ))}
                  </TeamList>
                ) : (
                  <AreaInfoValue>—</AreaInfoValue>
                )}
              </AreaInfoItem>
            </AreaInfoGrid>
          </AreaInfoCard>

          <EditorLayout>
            <FormGrid>
              <Field>
                Título
                <input
                  value={form.title}
                  onChange={onField("title")}
                  placeholder="Ex.: Terra Preta — Landsat 8"
                />
              </Field>
              <Field>
                Data da observação *
                <input
                  type="date"
                  value={form.observation_date}
                  onChange={onField("observation_date")}
                />
              </Field>
              <Field>
                Área indicada (ha)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.area_ha}
                  onChange={onField("area_ha")}
                  placeholder="Opcional"
                />
              </Field>
              <Field>
                Status
                <select value={form.status} onChange={onField("status")}>
                  <option value="draft">Rascunho</option>
                  <option value="confirmed">Confirmado</option>
                </select>
              </Field>
              {!isEdit && (
                <Field style={{ gridColumn: "1 / -1" }}>
                  Arquivo TIFF *
                  <input
                    type="file"
                    accept=".tif,.tiff,.geotiff,image/tiff"
                    onChange={(e) => setTiffFile(e.target.files?.[0] || null)}
                  />
                </Field>
              )}
              <Field style={{ gridColumn: "1 / -1" }}>
                Observações
                <textarea
                  value={form.notes}
                  onChange={onField("notes")}
                  placeholder="Notas sobre a indicação / comparação"
                />
              </Field>
            </FormGrid>

            {(tiffFile || tiffUrl) && (
              <TiffSuppressionEditor
                tiffFile={tiffFile}
                tiffUrl={tiffUrl}
                initialGeometry={geometry}
                onGeometryChange={handleGeometry}
              />
            )}

            {!isEdit && !tiffFile && (
              <Hint>Selecione um TIFF para abrir o painel de marcação.</Hint>
            )}

            <FormActions>
              <StyledButtonForm type="button" onClick={handleSave} disabled={saving}>
                {saving
                  ? "Salvando…"
                  : isEdit
                    ? "Salvar indicação"
                    : "Cadastrar supressão"}
              </StyledButtonForm>
              <StyledButtonForm type="button" secondary onClick={goBack}>
                Voltar
              </StyledButtonForm>
            </FormActions>
          </EditorLayout>
        </ContentWrapper>
      </SideContainer>
    </MainContainer>
  );
};

export default VegetationSuppressionForm;
